import { useFetcher } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { useCallback, useEffect } from 'react';
import type { FetcherData } from 'types';

import AppleIcon from '~/assets/icons/Apple';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';

import type { PostAuthOptions, ScreenProps } from '../..';

type Props = {
  onSuccess: () => void;
  postAuthOptions?: PostAuthOptions;
  setError: ScreenProps['setError'];
};

const Button = ({ onSuccess, postAuthOptions, setError }: Props) => {
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();

  const appleAuth = window?.AppleID?.auth;
  const errors = fetcher.data?.errors;
  const submitting = fetcher.state === 'submitting';

  async function onLoginClick() {
    let appleAuthToken = '';

    try {
      setError(null);
      if (!appleAuth) throw new Error('Apple auth not available');

      const { authorization } = await appleAuth?.signIn();

      appleAuthToken = authorization.id_token;
    } catch (error) {
      Sentry.captureException(error, {
        extra: { message: 'Apple auth failed' }
      });

      return setError(
        'Apple sign in failed. If on a social media browser, please open this page in your external browser. If not, please try again or use another method.'
      );
    }

    fetcher.submit(
      {
        action: postAuthOptions?.action ?? '',
        guestId: user.guestId ?? '',
        legacyProductId: postAuthOptions?.legacyProductId ?? '',
        redirectUrl: postAuthOptions?.redirectUrl ?? '',
        sizes: JSON.stringify(postAuthOptions?.sizes ?? []),
        token: appleAuthToken
      },
      { action: '/bolt/login/apple', method: 'post' }
    );

    gtm.trackedButtonClick({
      buttonName: 'SIGN_IN_APPLE',
      pagePosition: 'Sign in modal'
    });
  }

  const onSignIn = useCallback(() => {
    gtm.sendLoginEvent(user.id, LoginSource.Apple);

    braze.setUser(user.id);
    braze.logLoginEvent();

    onSuccess();
  }, [onSuccess, user.id]);

  useEffect(() => {
    if (errors?.message) setError(errors.message);
  }, [errors, setError]);

  useEffect(() => {
    if (fetcher.data?.success) onSignIn();
  }, [fetcher.data, onSignIn]);

  if (!appleAuth) return null;

  return (
    <button
      className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-concrete px-4 py-2 font-semibold"
      onClick={onLoginClick}
    >
      <AppleIcon colour="black" />
      <span className="hidden md:inline-block">
        {submitting ? 'Signing in...' : 'Apple'}
      </span>
    </button>
  );
};

export default Button;
